import React, { useEffect, useState } from 'react'
import { apiMiddleware, ApiError } from '../../services/apiMiddleware'
import { AlertCircle, RefreshCw, X } from 'lucide-react'
import { Button } from '../ui/button'
import { Card } from '../ui/card'

interface AuthErrorState {
  show: boolean
  error: ApiError | null
  isTokenExpired: boolean
}

export const AuthErrorHandler: React.FC = () => {
  const [errorState, setErrorState] = useState<AuthErrorState>({
    show: false,
    error: null,
    isTokenExpired: false
  })

  useEffect(() => {
    // Handle token expiration
    const handleTokenExpired = () => {
      setErrorState({
        show: true,
        error: null,
        isTokenExpired: true
      })
    }

    // Handle authentication errors
    const handleAuthError = (error: ApiError) => {
      // Only show non-401 errors here, as 401s are handled by token refresh
      if (error.status !== 401) {
        setErrorState({
          show: true,
          error,
          isTokenExpired: false
        })
      }
    }

    // Handle successful token refresh
    const handleTokenRefreshed = () => {
      setErrorState({
        show: false,
        error: null,
        isTokenExpired: false
      })
    }

    // Note: 'unauthorized' event is handled by apiMiddleware which already
    // sets sessionExpired flag and redirects. The Login page handles showing
    // the session expired message via toast.

    // Subscribe to auth events
    apiMiddleware.on('tokenExpired', handleTokenExpired)
    apiMiddleware.on('authError', handleAuthError)
    apiMiddleware.on('tokenRefreshed', handleTokenRefreshed)

    // Cleanup
    return () => {
      apiMiddleware.off('tokenExpired', handleTokenExpired)
      apiMiddleware.off('authError', handleAuthError)
      apiMiddleware.off('tokenRefreshed', handleTokenRefreshed)
    }
  }, [])

  const handleDismiss = () => {
    setErrorState({
      show: false,
      error: null,
      isTokenExpired: false
    })
  }

  const handleRetry = () => {
    handleDismiss()
    // Redirect to login instead of reloading the page
    window.location.href = '/login'
  }

  if (!errorState.show) return null

  const getErrorMessage = () => {
    if (errorState.isTokenExpired) {
      return errorState.error?.message || 'Your session has expired. Please refresh the page or login again.'
    }
    
    if (errorState.error?.status === 403) {
      return 'You do not have permission to access this resource.'
    }
    
    return errorState.error?.message || 'An authentication error occurred.'
  }

  const getErrorTitle = () => {
    if (errorState.isTokenExpired) {
      return 'Session Expired'
    }
    
    if (errorState.error?.status === 403) {
      return 'Access Denied'
    }
    
    return 'Authentication Error'
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md">
      <Card className="border-red-200 bg-red-50 text-red-800 shadow-lg">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-red-800 mb-1">{getErrorTitle()}</h4>
              <p className="text-sm text-red-700 mb-3">
                {getErrorMessage()}
              </p>
              
              {(errorState.isTokenExpired || errorState.error?.status === 403) && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetry}
                    className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100 bg-white"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                </div>
              )}
            </div>
            
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="h-6 w-6 p-0 text-red-600 hover:bg-red-100 flex-shrink-0"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default AuthErrorHandler
